import { LIBRO_COMPRAS_EXCLUDED_CODES } from "~/constants/siiDocTypes";
import { supabase } from "~/integrations/supabase/client";

import type {
  LibroComprasRow,
  SupplierInvoice,
  SupplierInvoiceFilters,
  SupplierInvoiceImportResult,
  SupplierInvoiceImportRow,
  SupplierInvoiceManualInput,
} from "~/types/purchasing";

// RPCs added after the last supabase types regeneration — thin typed wrapper (same approach as
// supplierBalanceService / inventoryVarianceService).
const rpcCall = supabase.rpc.bind(supabase) as unknown as <T>(
  fn: string,
  args?: Record<string, unknown>
) => Promise<{ data: T | null; error: { message: string } | null }>;

// company_id column + company embed postdate the generated supabase types — cast the builder.
const fromAny = supabase.from.bind(supabase) as unknown as (table: string) => any;

/** Dedup key for a DTE (emisor RUT + tipo + folio). */
export interface InvoiceKey {
  rut: string;
  doc_tipo_code: string;
  folio: string;
}

/** One line of the asiento draft (debit/credit already balanced by the caller). */
export interface PostingLine {
  account_id: string;
  description?: string | null;
  debit: number;
  credit: number;
}

export interface ContabilizarInput {
  invoiceId: string;
  moveDate: string; // ISO date (factura emisión)
  ref: string;
  lines: PostingLine[];
  /** Gasto account chosen for the neto — remembered as the proveedor's default (learn-as-you-go). */
  gastoAccountId?: string | null;
}

export const supplierInvoiceService = {
  /** Which of the given (rut, tipo, folio) keys are already loaded — for the preview. */
  async checkExisting(keys: InvoiceKey[]): Promise<InvoiceKey[]> {
    if (keys.length === 0) return [];
    const { data, error } = await rpcCall<InvoiceKey[]>(
      "check_supplier_invoices_exist",
      { p_keys: keys }
    );
    if (error) throw new Error(error.message);
    return data ?? [];
  },

  /** Upsert suppliers by RUT + insert the invoices idempotently for a company. Returns counts. */
  async createBatch(
    rows: SupplierInvoiceImportRow[],
    companyId: string
  ): Promise<SupplierInvoiceImportResult> {
    const { data, error } = await rpcCall<SupplierInvoiceImportResult>(
      "create_supplier_invoices_batch",
      { p_rows: rows, p_company_id: companyId }
    );
    if (error) throw new Error(error.message);
    return (
      data ?? { inserted: 0, skipped: 0, suppliers_created: 0, suppliers_rut_backfilled: 0 }
    );
  },

  /** Register one factura manually (known supplier the user picked). Returns the new invoice id.
   * Amounts must already carry the AP sign (facturas negative, NC positive). */
  async createManual(input: SupplierInvoiceManualInput): Promise<string> {
    const { data, error } = await rpcCall<string>(
      "create_supplier_invoice_manual",
      {
        p_supplier_id: input.supplierId,
        p_company_id: input.companyId,
        p_doc_tipo_code: input.docTipoCode,
        p_document_type: input.documentType ?? null,
        p_folio: input.folio,
        p_emission_date: input.emissionDate,
        p_due_date: input.dueDate ?? null,
        p_due_date_estimated: input.dueDateEstimated ?? false,
        p_exento: input.exento,
        p_neto: input.neto,
        p_iva: input.iva,
        p_impto_especifico: input.imptoEspecifico,
        p_total_clp: input.totalClp,
        p_saldo_clp: input.saldoClp,
        p_glosa: input.glosa ?? null,
        p_manual_pdf_url: input.manualPdfUrl ?? null,
      }
    );
    if (error) throw new Error(error.message);
    return data as string;
  },

  /** Libro de Compras (SII/F29) — a company's facturas for a period (neto/exento/iva/total).
   * Corr. Interno = move_number del asiento vinculado (número de comprobante). FEX (facturas
   * extranjeras) se excluyen: su importación ya entra al libro vía la DIN (914).
   * Solo facturas CONTABILIZADAS (asiento publicado y no eliminado) — mismo predicado que
   * get_cxp_reconciliation / aging — para que el total cuadre con el mayor en el cruce.
   * El período se asigna por la fecha del asiento (`move_date`) — el período en que se
   * declara/reconoce el IVA crédito fiscal — no por `emission_date`. Así una factura con
   * emisión en un mes previo pero contabilizada este mes (p. ej. comisiones de sept
   * recuperadas en oct) cae en el libro del mes del asiento, igual que el mayor en el cruce
   * (`get_cxp_reconciliation_docs` filtra por `move_date`). La columna "Fecha" sigue
   * mostrando la fecha de emisión del documento, como en Softland. */
  async libroCompras(
    companyId: string,
    from: string,
    to: string
  ): Promise<LibroComprasRow[]> {
    const excluded = LIBRO_COMPRAS_EXCLUDED_CODES.join(",");
    const { data, error } = await fromAny("supplier_invoices")
      .select(
        "id, emission_date, doc_tipo_code, folio, rut, razon_social, exento, neto, iva, impto_especifico, total_clp, movement:account_movements!inner(move_number, move_date)"
      )
      .eq("company_id", companyId)
      .eq("status", "registrada")
      .eq("movement.is_published", true)
      .eq("movement.is_deleted", false)
      .not("doc_tipo_code", "in", `(${excluded})`)
      .gte("movement.move_date", from)
      .lte("movement.move_date", to)
      .order("emission_date", { ascending: true });
    if (error) throw error;
    return (data ?? []).map((raw: Record<string, unknown>) => {
      const movement = raw.movement as { move_number: string | null } | null;
      return {
        ...(raw as Omit<LibroComprasRow, "corr_interno">),
        corr_interno: movement?.move_number ?? null,
      } as LibroComprasRow;
    });
  },

  /** Reassign a factura to another empresa (BSale doesn't carry it) — before contabilización. */
  async setCompany(invoiceId: string, companyId: string): Promise<void> {
    const { error } = await fromAny("supplier_invoices")
      .update({ company_id: companyId })
      .eq("id", invoiceId);
    if (error) throw error;
  },

  /** List registered supplier invoices (with supplier name), newest first. */
  async list(filters?: SupplierInvoiceFilters): Promise<SupplierInvoice[]> {
    let query = fromAny("supplier_invoices")
      .select(
        "*, supplier:suppliers(id, name), company:company_id(id, name), movement:account_movements(is_published, is_deleted)"
      )
      .eq("status", "registrada")
      .order("emission_date", { ascending: false })
      .limit(2000);

    if (filters?.company_id) query = query.eq("company_id", filters.company_id);
    if (filters?.doc_tipo_code) query = query.eq("doc_tipo_code", filters.doc_tipo_code);
    if (filters?.dateFrom) query = query.gte("emission_date", filters.dateFrom);
    if (filters?.dateTo) query = query.lte("emission_date", filters.dateTo);
    if (filters?.pdfStatus === "without") {
      query = query.is("url_pdf", null).is("manual_pdf_url", null);
    } else if (filters?.pdfStatus === "with") {
      query = query.or("url_pdf.not.is.null,manual_pdf_url.not.is.null");
    }
    if (filters?.search) {
      query = query.or(
        `razon_social.ilike.%${filters.search}%,folio.ilike.%${filters.search}%,rut.ilike.%${filters.search}%`
      );
    }

    const { data, error } = await query;
    if (error) throw error;
    // posting_status is DERIVED from the linked movement (no stored column / trigger): none or
    // deleted → sin_contabilizar · unpublished → borrador · published → contabilizada.
    return (data ?? []).map((raw) => {
      const row = raw as Record<string, unknown> & {
        account_movement_id: string | null;
        movement?: { is_published: boolean; is_deleted: boolean } | null;
      };
      const m = row.movement;
      const posting_status: SupplierInvoice["posting_status"] =
        !row.account_movement_id || !m || m.is_deleted
          ? "sin_contabilizar"
          : m.is_published
            ? "contabilizada"
            : "borrador";
      const result = { ...row, posting_status };
      delete (result as Record<string, unknown>).movement;
      return result as unknown as SupplierInvoice;
    });
  },

  /** Create the draft asiento (PROV journal) for a factura, link it, and remember the gasto per
   * proveedor. Returns the account_movements id. Idempotent (RPC rejects if already posted). */
  async contabilizar(input: ContabilizarInput): Promise<string> {
    const { data, error } = await rpcCall<string>(
      "contabilizar_supplier_invoice",
      {
        p_invoice_id: input.invoiceId,
        p_move_date: input.moveDate,
        p_ref: input.ref,
        p_lines: input.lines,
        p_gasto_account_id: input.gastoAccountId ?? null,
      }
    );
    if (error) throw new Error(error.message);
    return data as string;
  },

  /** Attach a manually-obtained PDF to a factura (upload public URL or pasted external URL).
   * Stored in manual_pdf_url — never touches the authoritative BSale url_pdf. */
  async attachManualPdf(invoiceId: string, url: string): Promise<void> {
    const { error } = await rpcCall<null>("attach_supplier_invoice_pdf", {
      p_invoice_id: invoiceId,
      p_url: url,
    });
    if (error) throw new Error(error.message);
  },

  /** Remove the manually-attached PDF from a factura (clears manual_pdf_url only — the
   * authoritative BSale url_pdf is never affected). */
  async removeManualPdf(invoiceId: string): Promise<void> {
    const { error } = await rpcCall<null>("remove_supplier_invoice_pdf", {
      p_invoice_id: invoiceId,
    });
    if (error) throw new Error(error.message);
  },
};
